import React, { useState, useEffect } from 'react';
import MUIDataTable from 'mui-datatables';
import { connect } from 'react-redux';
import * as proposalService from '../../../../services/service/proposalService';
import * as alert from '../../../../redux/alertSnackBarRedux';
import * as confirmModal from '../../../../redux/confirmModalRedux';
import { Link, NavLink } from 'react-router-dom';
import { dateFilterListRenderer, dateFilterLogic } from '../../../../helpers/helper';
import { Nav, Tab, Spinner, Modal, Form, Button } from 'react-bootstrap';
import DateFilterComponent from '../../../components/DateFilterComponent';
import SVG from 'react-inlinesvg';
import { toAbsoluteUrl } from '../../../../_metronic/_helpers';

const ProposalListingPage = (props) => {
  const { showAlert, showConfirmModal, hideConfirmModal, setProcessing } = props;

  const proposalToRejectInitialState = {
    projectNumber: '',
    proposalId: 0,
    rejectionReason: '',
  };

  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [secondTableProposals, setSecondTableProposals] = useState([]);
  const [status, setStatus] = useState('unassigned');
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [proposalToReject, setProposalToReject] = useState(proposalToRejectInitialState);

  useEffect(() => {
    loadProposalsForSecondTable();
    loadProposals(status);
  }, [status]);

  const loadProposals = () => {
    setLoading(true);
    proposalService
      .GetProposals(status)
      .then((res) => {
        setProposals(res);
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      })
      .finally((x) => setLoading(false));
  };

  const loadProposalsForSecondTable = () => {
    setLoading(true);
    proposalService
      .GetProposals('SENT-TO-CLIENT')
      .then((res) => {
        setSecondTableProposals(res);
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      })
      .finally((x) => setLoading(false));
  };

  const deleteProposal = (proposalId) => {
    if (!proposalId) {
      showAlert({ message: 'Unable to delete this proposal because its ID is missing.', variant: 'error' });
      return;
    }

    proposalService
      .DeleteProposal(proposalId)
      .then((res) => {
        showAlert({ message: res.message, variant: 'success' });
        hideConfirmModal();
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      });
  };

  const handleRejectionModalClose = () => {
    setIsRejectionModalOpen(false);
    setProposalToReject(proposalToRejectInitialState);
  };

  const handleProposalRejection = () => {
    if (!proposalToReject.proposalId) {
      showAlert({ message: 'Unable to reject this proposal because its ID is missing.', variant: 'error' });
      return;
    }

    proposalService
      .RejectProposal(proposalToReject)
      .then((res) => {
        loadProposalsForSecondTable();
        showAlert({ message: res.message, variant: 'success' });
        handleRejectionModalClose();
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      });
  };

  const handleProposalApproval = (proposalId) => {
    if (!proposalId) {
      showAlert({ message: 'Unable to approve this proposal because its ID is missing.', variant: 'error' });
      return;
    }

    setProcessing();
    proposalService
      .ApproveProposal(proposalId)
      .then((res) => {
        loadProposalsForSecondTable();
        showAlert({ message: res.message, variant: 'success' });
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      })
      .finally(() => hideConfirmModal());
  };

  const handleProposalRevision = (proposalId) => {
    if (!proposalId) {
      showAlert({ message: 'Unable to revise this proposal because its ID is missing.', variant: 'error' });
      return;
    }

    setProcessing();
    proposalService
      .ReviseProposal(proposalId)
      .then((res) => {
        loadProposalsForSecondTable();
        showAlert({ message: res.message, variant: 'success' });
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      })
      .finally(() => hideConfirmModal());
  };

  const getRejectionReason = (proposalId, projectNumber) => {
    if (!proposalId) {
      showAlert({ message: 'Unable to load the rejection reason because the proposal ID is missing.', variant: 'error' });
      return;
    }

    proposalService
      .GetRejectionReason(proposalId)
      .then((res) => {
        setProposalToReject({
          projectNumber: projectNumber,
          proposalId: proposalId,
          rejectionReason: '',
          savedRejectionReason: res.rejectionReason,
        });

        setIsRejectionModalOpen(true);
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      });
  };

  const proposalsColumn = [
    {
      name: 'id',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        width: 1,
        download: false,
        print: false,
        setCellProps: () => ({
          'data-aes-field': 'actions',
          'data-aes-label': 'Actions',
        }),
        customBodyRenderLite: (dataIndex) => {
          const proposal = proposals[dataIndex];
          if (!proposal || !proposal.id) return null;

          return (
            <div className='flex-container aes-admin-listing-actions'>
              <NavLink
                to={{
                  pathname: '/services/manage-proposal/' + proposal.id,
                  state: { proposalId: proposal.id },
                }}
                className='btn btn-sm btn-info font-weight-bold font-size-sm mr-3'
              >
                Edit
              </NavLink>
              <button
                className={`btn btn-danger btn-sm font-weight-bold font-size-sm mr-3`}
                onClick={() =>
                  showConfirmModal({
                    title: 'Delete Proposal',
                    text: 'Are you sure you want to delete this proposal?',
                    rightBtnText: 'Confirm',
                    btnAction: () => deleteProposal(proposal.id, false),
                  })
                }
              >
                Delete
              </button>
            </div>
          );
        },
      },
    },
    {
      name: 'projectNumber',
      label: 'Project #',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'project-number',
          'data-aes-label': 'Project Number',
          style: { width: '10%' },
        }),
        customBodyRenderLite: (dataIndex) =>
          proposals[dataIndex] && proposals[dataIndex].id ? (
            <Link
              to={{
                pathname: '/services/manage-proposal/' + proposals[dataIndex].id,
                state: { proposalId: proposals[dataIndex].id },
              }}
            >
              {proposals[dataIndex].projectNumber}
            </Link>
          ) : null,
      },
    },
    {
      name: 'client',
      label: 'Client',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'client',
          'data-aes-label': 'Client',
        }),
      },
    },
    // {
    //     name: "location",
    //     label: "Location",
    //     options: {
    //         filter: true,
    //         filterType: 'multiselect',
    //         sort: true,
    //     }
    // },
    {
      name: 'projectType',
      label: 'Project Type',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'project-type',
          'data-aes-label': 'Project Type',
        }),
      },
    },
    {
      name: 'projectSubType',
      label: 'Project Sub Type',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'project-sub-type',
          'data-aes-label': 'Project Sub Type',
        }),
      },
    },
    {
      name: 'createdBy',
      label: 'Created By',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'created-by',
          'data-aes-label': 'Created By',
        }),
      },
    },
    {
      name: 'expiryDate',
      label: 'Expiry Date',
      options: {
        display: status !== 'reviewed',
        filter: true,
        sort: true,
        filterType: 'custom',
        setCellProps: () => ({
          'data-aes-field': 'expiry-date',
          'data-aes-label': 'Expiry Date',
        }),
        customFilterListRender: (v) => dateFilterListRenderer(v),
        filterOptions: {
          names: [],
          logic(date, filters) {
            return dateFilterLogic(date, filters);
          },
          display: (filterList, onChange, index, column) => (
            <DateFilterComponent name={'Expiry Date'} filterList={filterList} onChange={onChange} index={index} column={column} />
          ),
        },
      },
    },
  ];

  const proposalsSecondTableColumn = [
    {
      name: 'id',
      label: 'Actions',
      options: {
        filter: true,
        sort: true,
        download: false,
        setCellProps: () => ({
          'data-aes-field': 'actions',
          'data-aes-label': 'Actions',
        }),
        customBodyRenderLite: (dataIndex) => {
          const proposal = secondTableProposals[dataIndex];
          if (!proposal || !proposal.id) return null;

          return (
            <div className='flex-container aes-admin-listing-actions'>
              {proposal.isApproved === false ? (
                <>
                  <button
                    onClick={() => getRejectionReason(proposal.id, proposal.projectNumber)}
                    className='btn btn-sm btn-info font-weight-bold font-size-sm mr-3'
                  >
                    View Rejection Reason
                  </button>
                </>
              ) : (
                <>
                  <button
                    className='btn btn-sm btn-primary font-weight-bold font-size-sm mr-3'
                    disabled={proposal.isApproved !== null}
                    onClick={() =>
                      proposal.isApproved === null
                        ? showConfirmModal({
                            title: 'Approve Proposal',
                            text: 'Are you sure you want to approve this proposal?',
                            rightBtnText: 'Confirm',
                            btnAction: () => handleProposalApproval(proposal.id),
                          })
                        : {}
                    }
                  >
                    Approved
                  </button>
                  <button
                    className='btn btn-sm btn-warning font-weight-bold font-size-sm mr-3'
                    onClick={() =>
                      showConfirmModal({
                        title: 'Revise Proposal',
                        text: 'Are you sure you want to revise this proposal?',
                        rightBtnText: 'Confirm',
                        btnAction: () => handleProposalRevision(proposal.id),
                      })
                    }
                  >
                    Revise
                  </button>
                  <button
                    disabled={proposal.isApproved !== null}
                    onClick={() => {
                      if (proposal.isApproved === null) {
                        setProposalToReject({
                          proposalId: proposal.id,
                          rejectionReason: '',
                          projectNumber: proposal.projectNumber,
                        });
                        setIsRejectionModalOpen(true);
                      }
                    }}
                    className={`btn btn-danger btn-sm font-weight-bold font-size-sm mr-3`}
                  >
                    Rejected
                  </button>
                </>
              )}
            </div>
          );
        },
      },
    },
    {
      name: 'projectNumber',
      label: 'Project #',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'project-number',
          'data-aes-label': 'Project Number',
          style: { width: '10%' },
        }),
        customBodyRenderLite: (dataIndex) =>
          secondTableProposals[dataIndex] && secondTableProposals[dataIndex].id ? (
            <Link
              to={{
                pathname: '/services/manage-proposal/' + secondTableProposals[dataIndex].id,
                state: { proposalId: secondTableProposals[dataIndex].id },
              }}
            >
              {secondTableProposals[dataIndex].projectNumber}
            </Link>
          ) : null,
      },
    },
    {
      name: 'client',
      label: 'Client',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'client',
          'data-aes-label': 'Client',
        }),
      },
    },
    // {
    //     name: "location",
    //     label: "Location",
    //     options: {
    //         filter: true,
    //         filterType: 'multiselect',
    //         sort: true,
    //     }
    // },
    {
      name: 'projectType',
      label: 'Project Type',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'project-type',
          'data-aes-label': 'Project Type',
        }),
      },
    },
    {
      name: 'projectSubType',
      label: 'Project Sub Type',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'project-sub-type',
          'data-aes-label': 'Project Sub Type',
        }),
      },
    },
    {
      name: 'createdBy',
      label: 'Created By',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'created-by',
          'data-aes-label': 'Created By',
        }),
      },
    },
    {
      name: 'isApproved',
      label: 'Is Approved',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'status',
          'data-aes-label': 'Status',
        }),
        customBodyRenderLite: (dataIndex) => {
          return (
            <>
              {secondTableProposals[dataIndex].isApproved === null ? (
                <p>Pending</p>
              ) : (
                <SVG
                  className='h-50 align-self-center'
                  src={toAbsoluteUrl(
                    secondTableProposals[dataIndex].isApproved
                      ? '/media/svg/icons/Navigation/Check.svg'
                      : '/media/svg/icons/Navigation/Close.svg',
                  )}
                ></SVG>
              )}
            </>
          );
        },
      },
    },
  ];

  return (
    <div className='row aes-admin-listing-page aes-admin-listing-page--proposals'>
      <Modal centered size='lg' show={isRejectionModalOpen} onHide={handleRejectionModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Rejection Reason for Project #{proposalToReject.projectNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {proposalToReject.savedRejectionReason ? (
            <p>{proposalToReject.savedRejectionReason}</p>
          ) : (
            <div className='form-group row'>
              <div className='col-lg-12 col-xl-12'>
                <label>Please enter the rejection reason in the text box below</label>
              </div>
              <div className='col-lg-12 col-xl-12'>
                <div className='input-group input-group-lg input-group-solid'>
                  <Form.Control
                    as='textarea'
                    rows='7'
                    placeholder='Enter Rejection Reason'
                    className={`form-control form-control-lg form-control-solid`}
                    name='rejectionReason'
                    value={proposalToReject.rejectionReason}
                    onChange={(e) => setProposalToReject({ ...proposalToReject, rejectionReason: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleRejectionModalClose}>
            Close
          </Button>
          {!proposalToReject.savedRejectionReason && (
            <button
              type='submit'
              className='btn btn-success mr-2'
              disabled={proposalToReject.rejectionReason.length === 0}
              onClick={handleProposalRejection}
            >
              Submit Rejection
            </button>
          )}
        </Modal.Footer>
      </Modal>

      <div className='col-lg-12 col-xxl-12 mb-10 aes-proposal-listing-section aes-proposal-listing-section--pending'>
        <div className={`card card-custom card-stretch gutter-b}`}>
          <div className={`card card-custom card-stretch gutter-b`}>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label font-weight-bolder text-dark'>Pending Proposals</span>
                <span className='text-muted mt-3 font-weight-bold fz-xs'>
                  View proposals that are still pending or being reviewed to be sent to clients.
                </span>
              </h3>
              <div className='card-toolbar'>
                <Tab.Container defaultActiveKey={status}>
                  <Nav
                    as='ul'
                    onSelect={(_status) => setStatus(_status)}
                    className='nav nav-pills nav-pills-sm nav-dark-75 aes-proposal-listing-tabs'
                  >
                    <Nav.Item className='nav-item' as='li'>
                      <Nav.Link eventKey='unassigned' className={`nav-link py-2 px-4 ${status === 'unassigned' ? 'active' : ''}`}>
                        Pending Review
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item className='nav-item' as='li'>
                      <Nav.Link eventKey='pending-review' className={`nav-link py-2 px-4 ${status === 'pending-review' ? 'active' : ''}`}>
                        Review In Progress
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Tab.Container>
              </div>
            </div>
            <div className='container-fluid aes-admin-listing aes-proposal-listing aes-proposal-listing--pending'>
              <MUIDataTable
                className='aes-admin-listing-surface aes-proposal-listing-surface aes-proposal-listing-surface--pending'
                data={proposals}
                columns={proposalsColumn}
                options={{
                  responsive: 'standard',
                  rowsPerPage: 50,
                  rowsPerPageOptions: [10, 15, 20, 25, 30, 35, 40, 45, 50, 100],
                  selectableRows: 'none',
                  setTableProps: () => ({
                    className: 'aes-admin-listing-table aes-proposal-listing-table aes-proposal-listing-table--pending',
                  }),
                  textLabels: {
                    body: {
                      noMatch: loading ? <Spinner animation='border' size='lg' variant='info' /> : 'Sorry, no matching records found.',
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className='col-lg-12 col-xxl-12 aes-proposal-listing-section aes-proposal-listing-section--submitted'>
        <div className={`card card-custom card-stretch gutter-b}`}>
          <div className='card-header border-0 pt-10'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label font-weight-bolder text-dark'>Submitted Proposals</span>
              <span className='text-muted mt-3 font-weight-bold fz-xs'>View all proposals sent to clients.</span>
            </h3>
          </div>
          <div className='container-fluid mt-lg-10 aes-admin-listing aes-proposal-listing aes-proposal-listing--submitted'>
            <MUIDataTable
              className='aes-admin-listing-surface aes-proposal-listing-surface aes-proposal-listing-surface--submitted'
              data={secondTableProposals}
              columns={proposalsSecondTableColumn}
              options={{
                responsive: 'standard',
                rowsPerPage: 50,
                rowsPerPageOptions: [10, 15, 20, 25, 30, 35, 40, 45, 50, 100],
                selectableRows: 'none',
                setTableProps: () => ({
                  className: 'aes-admin-listing-table aes-proposal-listing-table aes-proposal-listing-table--submitted',
                }),
                textLabels: {
                  body: {
                    noMatch: loading ? <Spinner animation='border' size='lg' variant='info' /> : 'Sorry, no matching records found.',
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(null, { ...alert.actions, ...confirmModal.actions })(ProposalListingPage);
